import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ChevronLeft,
  ChevronRight,
  Filter,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Trash2,
  Package,
} from "lucide-react";
import {
  addReturnsFromOrder,
  createReturn,
  deleteAllAvailableReturns,
  deleteReturn,
  fetchReturns,
  previewOrderReturns,
  rejectReturn,
  restoreReturn,
  updateReturn,
} from "../api/ReturnsAPIs";
import { fetchCaseStylesForType, fetchTypes } from "../api/TypeAPIs";
import { CaseStyleBadge } from "../Components/orders/CaseStyleBadge";
import { useAuth } from "../utils/auth";
import { canDeleteAllReturns, canMutateReturns, canViewReturns, isAdmin } from "../utils/roles";
import { displayAr } from "./Catalog/catalogUi";
import Select from "react-select";

const PAGE_SIZE = 20;
const DELETE_ALL_CODE = "252001";

const STATUS_TABS = [
  { key: "Available", label: "متاح" },
  { key: "Reserved", label: "محجوز" },
  { key: "Consumed", label: "مستهلك" },
  { key: "Rejected", label: "مرفوض / تالف" },
  { key: "All", label: "الكل" },
];

const statusBadge = (status) => {
  switch (status) {
    case "Available":
      return "bg-emerald-100 text-emerald-800";
    case "Reserved":
      return "bg-amber-100 text-amber-800";
    case "Consumed":
      return "bg-slate-200 text-slate-700";
    case "Rejected":
      return "bg-rose-100 text-rose-800";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

const statusLabel = (status) => {
  switch (status) {
    case "Available":
      return "متاح";
    case "Reserved":
      return "محجوز";
    case "Consumed":
      return "مستهلك";
    case "Rejected":
      return "مرفوض / تالف";
    default:
      return status || "—";
  }
};

const formatDateTime = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString("ar-EG");
};

const typeSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: "42px",
    borderRadius: "0.75rem",
    borderColor: state.isFocused ? "#0284c7" : "#e2e8f0",
    boxShadow: "none",
    direction: "rtl",
  }),
  menu: (provided) => ({
    ...provided,
    direction: "rtl",
    zIndex: 80,
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 80,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#0284c7"
      : state.isFocused
        ? "#e0f2fe"
        : "white",
    color: state.isSelected ? "white" : "#111827",
    textAlign: "right",
    cursor: "pointer",
  }),
  singleValue: (provided) => ({
    ...provided,
    textAlign: "right",
  }),
  placeholder: (provided) => ({
    ...provided,
    textAlign: "right",
    color: "#94a3b8",
  }),
  input: (provided) => ({
    ...provided,
    direction: "rtl",
  }),
};

const TypeSearchSelect = ({
  options,
  value,
  onChange,
  placeholder,
  isClearable = false,
  isDisabled = false,
  className = "",
}) => (
  <Select
    className={className}
    options={options}
    value={options.find((opt) => String(opt.value) === String(value || "")) || null}
    onChange={(selected) => onChange(selected?.value || "")}
    placeholder={placeholder}
    isSearchable
    isClearable={isClearable}
    isDisabled={isDisabled}
    styles={typeSelectStyles}
    noOptionsMessage={() => "لا توجد نتائج مطابقة"}
    filterOption={(option, input) =>
      option.label.toLowerCase().includes((input || "").trim().toLowerCase())
    }
    menuPlacement="auto"
    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
    menuPosition="fixed"
  />
);

const emptyAddForm = { designCode: "", typeId: "", caseStyleId: "", orderId: "", notes: "", rejected: false };

const caseStyleSelectOptions = (styles) =>
  (styles || []).map((style) => ({
    value: String(style.caseStyleId),
    label: displayAr(style.name) || `نوع ${style.caseStyleId}`,
  }));

const pickDefaultCaseStyleId = (styles) => {
  if (!styles?.length) return "";
  if (styles.length === 1) return String(styles[0].caseStyleId);
  return "";
};

const ReturnsPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const queryClient = useQueryClient();
  const canMutate = canMutateReturns(authState.role);
  const canDeleteAll = canDeleteAllReturns(authState.role);

  const [page, setPage] = useState(0);
  const [status, setStatus] = useState("Available");
  const [filters, setFilters] = useState({
    designCode: "",
    typeId: "",
    orderId: "",
    fromUtc: "",
    toUtc: "",
  });
  const [applied, setApplied] = useState(filters);
  const [showAdd, setShowAdd] = useState(false);
  const [showOrder, setShowOrder] = useState(false);
  const [showDeleteAll, setShowDeleteAll] = useState(false);
  const [addForm, setAddForm] = useState(emptyAddForm);
  const [editItem, setEditItem] = useState(null);
  const [orderIdInput, setOrderIdInput] = useState("");
  const [orderNotes, setOrderNotes] = useState("");
  const [orderPreview, setOrderPreview] = useState(null);
  const [deleteAllInput, setDeleteAllInput] = useState("");
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const orderId = searchParams.get("orderId");
    if (!orderId || !/^\d+$/.test(orderId)) return;
    setFilters((current) => ({ ...current, orderId }));
    setApplied((current) => ({ ...current, orderId }));
    setStatus("All");
  }, [searchParams]);

  useEffect(() => {
    if (!canViewReturns(authState.role)) {
      navigate("/login");
    }
  }, [authState.role, navigate]);

  const { data: types = [] } = useQuery({
    queryKey: ["phoneTypes"],
    queryFn: fetchTypes,
    staleTime: 10 * 60 * 1000,
  });

  const addTypeId = addForm.typeId;
  const editTypeId = editItem?.typeId;
  const { data: addCaseStyles = [] } = useQuery({
    queryKey: ["caseStylesForType", String(addTypeId || "")],
    queryFn: () => fetchCaseStylesForType(addTypeId),
    enabled: !!addTypeId,
    staleTime: 10 * 60 * 1000,
  });
  const { data: editCaseStyles = [] } = useQuery({
    queryKey: ["caseStylesForType", String(editTypeId || "")],
    queryFn: () => fetchCaseStylesForType(editTypeId),
    enabled: !!editTypeId,
    staleTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    if (!addTypeId) {
      setAddForm((current) => (current.caseStyleId ? { ...current, caseStyleId: "" } : current));
      return;
    }
    const allowed = new Set(addCaseStyles.map((style) => String(style.caseStyleId)));
    const fallback = pickDefaultCaseStyleId(addCaseStyles);
    setAddForm((current) => {
      if (allowed.has(String(current.caseStyleId))) return current;
      return { ...current, caseStyleId: fallback };
    });
  }, [addTypeId, addCaseStyles]);

  useEffect(() => {
    if (!editTypeId) {
      setEditItem((current) => (current?.caseStyleId ? { ...current, caseStyleId: "" } : current));
      return;
    }
    const allowed = new Set(editCaseStyles.map((style) => String(style.caseStyleId)));
    const fallback = pickDefaultCaseStyleId(editCaseStyles);
    setEditItem((current) => {
      if (!current) return current;
      if (allowed.has(String(current.caseStyleId))) return current;
      return { ...current, caseStyleId: fallback || current.caseStyleId || "" };
    });
  }, [editTypeId, editCaseStyles]);

  const returnsQuery = useQuery({
    queryKey: ["returns", page, status, applied],
    queryFn: () =>
      fetchReturns({
        page,
        pageSize: PAGE_SIZE,
        status,
        designCode: applied.designCode,
        typeId: applied.typeId,
        orderId: applied.orderId,
        fromUtc: applied.fromUtc ? new Date(applied.fromUtc).toISOString() : "",
        toUtc: applied.toUtc ? new Date(applied.toUtc).toISOString() : "",
      }),
    keepPreviousData: true,
  });

  const returns = returnsQuery.data?.items || [];
  const totalCount = returnsQuery.data?.totalCount || 0;
  const hasMore = !!returnsQuery.data?.hasMore;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["returns"] });

  const addMutation = useMutation({
    mutationFn: createReturn,
    onSuccess: () => {
      toast.success("تم إضافة المرتجع");
      setShowAdd(false);
      setAddForm(emptyAddForm);
      invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const editMutation = useMutation({
    mutationFn: ({ id, payload }) => updateReturn(id, payload),
    onSuccess: () => {
      toast.success("تم تعديل المرتجع");
      setEditItem(null);
      invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: ({ id, force }) => deleteReturn(id, { force }),
    onSuccess: () => {
      toast.success("تم حذف المرتجع");
      invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, notes }) => rejectReturn(id, notes),
    onSuccess: () => {
      toast.success("تم تعليم المرتجع كمرفوض/تالف");
      invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const restoreMutation = useMutation({
    mutationFn: restoreReturn,
    onSuccess: () => {
      toast.success("تم إرجاع القطعة إلى المتاح");
      invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const previewMutation = useMutation({
    mutationFn: previewOrderReturns,
    onSuccess: (data) => setOrderPreview(data),
    onError: (err) => {
      setOrderPreview(null);
      toast.error(err.message);
    },
  });

  const fromOrderMutation = useMutation({
    mutationFn: () => addReturnsFromOrder(orderIdInput, orderNotes),
    onSuccess: (data) => {
      toast.success(`تم إنشاء ${data.createdCount} مرتجع`);
      if (data.skippedDuplicates) toast.info(`تم تخطي ${data.skippedDuplicates} قطعة مستوردة مسبقاً`);
      setShowOrder(false);
      setOrderIdInput("");
      setOrderNotes("");
      setOrderPreview(null);
      invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteAllMutation = useMutation({
    mutationFn: deleteAllAvailableReturns,
    onSuccess: (data) => {
      toast.success(`تم حذف ${data.deletedCount} مرتجع متاح`);
      if (data.skippedReserved || data.skippedConsumed || data.skippedRejected) {
        toast.info(
          `تم الإبقاء على ${data.skippedReserved || 0} محجوز و ${data.skippedConsumed || 0} مستهلك و ${data.skippedRejected || 0} مرفوض`
        );
      }
      setShowDeleteAll(false);
      setDeleteAllInput("");
      invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const typeOptions = useMemo(
    () =>
      [...types].sort((a, b) =>
        displayAr(a.name).localeCompare(displayAr(b.name), "ar")
      ),
    [types]
  );

  const typeSelectOptions = useMemo(
    () =>
      typeOptions.map((t) => ({
        value: String(t.typeId),
        label: displayAr(t.name),
      })),
    [typeOptions]
  );

  const applyFilters = (e) => {
    e?.preventDefault();
    setPage(0);
    setApplied({ ...filters });
  };

  const handleDelete = (item) => {
    const safe = item.status === "Available" || item.status === "Rejected";
    if (!safe && !isAdmin(authState.role)) {
      toast.error("لا يمكن حذف مرتجع محجوز أو مستهلك");
      return;
    }
    const extra = safe
      ? `حذف المرتجع #${item.returnId}؟`
      : `هذا المرتجع ${statusLabel(item.status)}. الحذف يتجاوز دورة الإنتاج. اكتب للتأكيد؟`;
    if (!window.confirm(extra)) return;
    deleteMutation.mutate({ id: item.returnId, force: !safe });
  };

  if (!canViewReturns(authState.role)) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-sky-50/50 to-slate-100 p-4 md:p-6" dir="rtl">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="max-w-7xl mx-auto space-y-5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800 flex items-center gap-2">
              <RotateCcw className="text-sky-600" />
              مخزون المرتجعات
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              قطعة مرتجعة واحدة لكل سجل — المطابقة تتم بالتصميم + نوع الموبايل + نوع الجراب
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canDeleteAll && (
              <button
                type="button"
                onClick={() => {
                  setDeleteAllInput("");
                  setShowDeleteAll(true);
                }}
                className="px-4 py-2.5 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700"
              >
                حذف كل المرتجعات المتاحة
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
            >
              رجوع
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-4">
          <div className="flex flex-wrap gap-2">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  setStatus(tab.key);
                  setPage(0);
                }}
                className={`px-4 py-2 rounded-xl text-sm font-semibold border ${
                  status === tab.key
                    ? "bg-sky-600 text-white border-sky-600"
                    : "bg-white text-slate-600 border-slate-200 hover:border-sky-300"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <form onSubmit={applyFilters} className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <input
              className="md:col-span-1 p-2.5 border rounded-xl"
              placeholder="كود التصميم"
              value={filters.designCode}
              onChange={(e) => setFilters((f) => ({ ...f, designCode: e.target.value }))}
            />
            <TypeSearchSelect
              className="min-w-0"
              options={typeSelectOptions}
              value={filters.typeId}
              onChange={(typeId) => setFilters((f) => ({ ...f, typeId }))}
              placeholder="ابحث عن نوع الموبايل..."
              isClearable
            />
            <input
              className="p-2.5 border rounded-xl"
              placeholder="رقم الطلب"
              value={filters.orderId}
              onChange={(e) => setFilters((f) => ({ ...f, orderId: e.target.value }))}
            />
            <input
              type="datetime-local"
              className="p-2.5 border rounded-xl"
              value={filters.fromUtc}
              onChange={(e) => setFilters((f) => ({ ...f, fromUtc: e.target.value }))}
            />
            <input
              type="datetime-local"
              className="p-2.5 border rounded-xl"
              value={filters.toUtc}
              onChange={(e) => setFilters((f) => ({ ...f, toUtc: e.target.value }))}
            />
            <button
              type="submit"
              className="flex items-center justify-center gap-2 p-2.5 rounded-xl bg-slate-800 text-white font-semibold"
            >
              <Search size={16} />
              بحث
            </button>
          </form>

          {canMutate && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setShowAdd(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700"
              >
                <Plus size={16} />
                إضافة مرتجع
              </button>
              <button
                type="button"
                onClick={() => {
                  setOrderPreview(null);
                  setShowOrder(true);
                }}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-sky-600 text-white font-semibold hover:bg-sky-700"
              >
                <Package size={16} />
                إضافة مرتجعات الطلب
              </button>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {returnsQuery.isLoading ? (
            <div className="p-8 text-center text-slate-500">جاري التحميل...</div>
          ) : returnsQuery.error ? (
            <div className="p-8 text-center text-red-600">{returnsQuery.error.message}</div>
          ) : returns.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <Filter className="mx-auto mb-3 opacity-40" />
              لا توجد مرتجعات مطابقة للفلتر الحالي
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="p-3 text-right">#</th>
                    <th className="p-3 text-right">كود التصميم</th>
                    <th className="p-3 text-right">التصميم</th>
                    <th className="p-3 text-right">نوع الموبايل</th>
                    <th className="p-3 text-right">نوع الجراب</th>
                    <th className="p-3 text-right">الطلب الأصلي</th>
                    <th className="p-3 text-right">القطعة</th>
                    <th className="p-3 text-right">حالة القطعة / العيب</th>
                    <th className="p-3 text-right">تاريخ الإضافة</th>
                    <th className="p-3 text-right">الحالة</th>
                    <th className="p-3 text-right">إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {returns.map((item) => (
                    <tr key={item.returnId} className="border-t hover:bg-slate-50">
                      <td className="p-3 font-semibold">{item.returnId}</td>
                      <td className="p-3 font-mono">{item.designCode || "—"}</td>
                      <td className="p-3">{displayAr(item.designName)}</td>
                      <td className="p-3">{displayAr(item.typeName)}</td>
                      <td className="p-3">
                        <CaseStyleBadge name={item.caseStyleName} />
                      </td>
                      <td className="p-3">{item.orderId || "—"}</td>
                      <td className="p-3">
                        {item.orderDetailsId
                          ? `${item.orderDetailsId} / ${item.unitIndexInLine || 1}`
                          : "—"}
                      </td>
                      <td className="p-3 max-w-xs truncate" title={item.notes || ""}>
                        {item.notes || "—"}
                      </td>
                      <td className="p-3 whitespace-nowrap">{formatDateTime(item.createdAt)}</td>
                      <td className="p-3">
                        <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${statusBadge(item.status)}`}>
                          {statusLabel(item.status)}
                        </span>
                        {item.status === "Reserved" && (
                          <p className="text-[11px] text-amber-800 mt-1">
                            إنتاج {item.reservedPrintQueueItemId || "—"}
                            {item.reservedPrintBatchId ? ` / دفعة ${item.reservedPrintBatchId}` : ""}
                            {item.reservedByUserId ? ` / مستخدم ${item.reservedByUserId}` : ""}
                          </p>
                        )}
                        {item.status === "Consumed" && (
                          <p className="text-[11px] text-slate-600 mt-1">
                            استُهلك {formatDateTime(item.consumedAt)}
                            {item.consumedPrintBatchId ? ` / دفعة ${item.consumedPrintBatchId}` : ""}
                            {item.consumedPrintQueueItemId ? ` / قطعة ${item.consumedPrintQueueItemId}` : ""}
                            {item.consumedByUserId ? ` / مستخدم ${item.consumedByUserId}` : ""}
                          </p>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          {canMutate && item.status === "Available" && (
                            <button
                              type="button"
                              onClick={() =>
                                setEditItem({
                                  ...item,
                                  designCode: item.designCode || "",
                                  typeId: item.typeId || "",
                                  caseStyleId: item.caseStyleId || "",
                                  orderId: item.orderId || "",
                                  notes: item.notes || "",
                                })
                              }
                              className="p-2 rounded-lg bg-sky-50 text-sky-700 hover:bg-sky-100"
                              title="تعديل"
                            >
                              <Pencil size={15} />
                            </button>
                          )}
                          {canMutate && item.status === "Available" && (
                            <button
                              type="button"
                              onClick={() => {
                                const notes = window.prompt(
                                  "سبب الرفض / حالة القطعة (مثال: خدش في الحافة)",
                                  item.notes || ""
                                );
                                if (notes == null) return;
                                if (!notes.trim()) {
                                  toast.error("المرتجع المرفوض يحتاج وصف للعيب");
                                  return;
                                }
                                rejectMutation.mutate({ id: item.returnId, notes });
                              }}
                              className="px-2 py-1 rounded-lg bg-rose-50 text-rose-700 text-xs font-bold hover:bg-rose-100"
                              title="مرفوض / تالف"
                            >
                              تالف
                            </button>
                          )}
                          {canMutate && item.status === "Rejected" && (
                            <button
                              type="button"
                              onClick={() => restoreMutation.mutate(item.returnId)}
                              className="px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-bold hover:bg-emerald-100"
                            >
                              إتاحة
                            </button>
                          )}
                          {canMutate && (item.status === "Available" || item.status === "Rejected" || isAdmin(authState.role)) && (
                            <button
                              type="button"
                              onClick={() => handleDelete(item)}
                              className="p-2 rounded-lg bg-red-50 text-red-700 hover:bg-red-100"
                              title="حذف"
                            >
                              <Trash2 size={15} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between px-4 py-3 border-t text-sm text-slate-600">
            <span>
              {totalCount} سجل — صفحة {page + 1}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="px-3 py-1.5 rounded-lg border disabled:opacity-40"
              >
                <ChevronRight size={16} />
              </button>
              <button
                type="button"
                disabled={!hasMore}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 rounded-lg border disabled:opacity-40"
              >
                <ChevronLeft size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {showAdd && (
        <Modal title="إضافة مرتجع واحد" onClose={() => setShowAdd(false)}>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (!addForm.designCode.trim() || !addForm.typeId) {
                toast.error("كود التصميم ونوع الموبايل مطلوبان");
                return;
              }
              if (addCaseStyles.length > 1 && !addForm.caseStyleId) {
                toast.error("اختار نوع الجراب");
                return;
              }
              if (addForm.rejected && !addForm.notes.trim()) {
                toast.error("المرتجع المرفوض يحتاج وصف للعيب");
                return;
              }
              addMutation.mutate(addForm);
            }}
          >
            <Field label="كود التصميم">
              <input
                required
                className="w-full p-2.5 border rounded-xl"
                value={addForm.designCode}
                onChange={(e) => setAddForm((f) => ({ ...f, designCode: e.target.value }))}
              />
            </Field>
            <Field label="نوع الموبايل">
              <TypeSearchSelect
                options={typeSelectOptions}
                value={addForm.typeId}
                onChange={(typeId) => setAddForm((f) => ({ ...f, typeId, caseStyleId: "" }))}
                placeholder="ابحث واختر نوع الموبايل..."
              />
            </Field>
            <Field label="نوع الجراب">
              <TypeSearchSelect
                options={caseStyleSelectOptions(addCaseStyles)}
                value={addForm.caseStyleId}
                onChange={(caseStyleId) => setAddForm((f) => ({ ...f, caseStyleId }))}
                isDisabled={!addForm.typeId}
                placeholder={
                  !addForm.typeId
                    ? "اختار نوع الموبايل أولاً"
                    : addCaseStyles.length === 0
                      ? "لا يوجد نوع جراب لهذا الموبايل"
                      : "اختار نوع الجراب..."
                }
              />
            </Field>
            <Field label="رقم الطلب (اختياري)">
              <input
                className="w-full p-2.5 border rounded-xl"
                value={addForm.orderId}
                onChange={(e) => setAddForm((f) => ({ ...f, orderId: e.target.value }))}
              />
            </Field>
            <Field label="حالة القطعة / العيب">
              <textarea
                className="w-full p-2.5 border rounded-xl"
                rows={2}
                placeholder='مثال: خدش في الحافة'
                value={addForm.notes}
                onChange={(e) => setAddForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={addForm.rejected}
                onChange={(e) => setAddForm((f) => ({ ...f, rejected: e.target.checked }))}
              />
              غير صالح للإنتاج (مرفوض / تالف)
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 rounded-xl bg-slate-100">
                إلغاء
              </button>
              <button
                type="submit"
                disabled={addMutation.isPending}
                className="px-4 py-2 rounded-xl bg-emerald-600 text-white font-semibold"
              >
                {addMutation.isPending ? "جاري الإضافة..." : "إضافة"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {editItem && (
        <Modal title={`تعديل المرتجع #${editItem.returnId}`} onClose={() => setEditItem(null)}>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              editMutation.mutate({
                id: editItem.returnId,
                payload: {
                  designCode: editItem.designCode,
                  typeId: editItem.typeId,
                  caseStyleId: editItem.caseStyleId,
                  orderId: editItem.orderId === "" ? 0 : editItem.orderId,
                  notes: editItem.notes,
                },
              });
            }}
          >
            <Field label="كود التصميم">
              <input
                required
                className="w-full p-2.5 border rounded-xl"
                value={editItem.designCode}
                onChange={(e) => setEditItem((f) => ({ ...f, designCode: e.target.value }))}
              />
            </Field>
            <Field label="نوع الموبايل">
              <TypeSearchSelect
                options={typeSelectOptions}
                value={editItem.typeId}
                onChange={(typeId) => setEditItem((f) => ({ ...f, typeId, caseStyleId: "" }))}
                placeholder="ابحث واختر نوع الموبايل..."
              />
            </Field>
            <Field label="نوع الجراب">
              <TypeSearchSelect
                options={caseStyleSelectOptions(editCaseStyles)}
                value={editItem.caseStyleId}
                onChange={(caseStyleId) => setEditItem((f) => ({ ...f, caseStyleId }))}
                isDisabled={!editItem.typeId}
                placeholder={
                  !editItem.typeId
                    ? "اختار نوع الموبايل أولاً"
                    : editCaseStyles.length === 0
                      ? "لا يوجد نوع جراب لهذا الموبايل"
                      : "اختار نوع الجراب..."
                }
              />
            </Field>
            <Field label="رقم الطلب (اختياري)">
              <input
                className="w-full p-2.5 border rounded-xl"
                value={editItem.orderId}
                onChange={(e) => setEditItem((f) => ({ ...f, orderId: e.target.value }))}
              />
            </Field>
            <Field label="حالة القطعة / العيب">
              <textarea
                className="w-full p-2.5 border rounded-xl"
                rows={2}
                value={editItem.notes}
                onChange={(e) => setEditItem((f) => ({ ...f, notes: e.target.value }))}
              />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEditItem(null)} className="px-4 py-2 rounded-xl bg-slate-100">
                إلغاء
              </button>
              <button
                type="submit"
                disabled={editMutation.isPending}
                className="px-4 py-2 rounded-xl bg-sky-600 text-white font-semibold"
              >
                حفظ
              </button>
            </div>
          </form>
        </Modal>
      )}

      {showOrder && (
        <Modal title="إضافة مرتجعات من طلب" onClose={() => setShowOrder(false)}>
          <div className="space-y-3">
            <Field label="رقم الطلب">
              <div className="flex gap-2">
                <input
                  className="flex-1 p-2.5 border rounded-xl"
                  value={orderIdInput}
                  onChange={(e) => setOrderIdInput(e.target.value)}
                />
                <button
                  type="button"
                  disabled={previewMutation.isPending || !orderIdInput}
                  onClick={() => previewMutation.mutate(Number(orderIdInput))}
                  className="px-4 py-2 rounded-xl bg-slate-800 text-white"
                >
                  معاينة
                </button>
              </div>
            </Field>
            <Field label="حالة القطع / العيب (اختياري)">
              <textarea
                className="w-full p-2.5 border rounded-xl"
                rows={2}
                placeholder='مثال: الجراب فيه عيب بسيط'
                value={orderNotes}
                onChange={(e) => setOrderNotes(e.target.value)}
              />
            </Field>
            {orderPreview && (
              <div className="rounded-xl bg-slate-50 border p-3 text-sm space-y-2">
                <p>قطع سيتم إنشاؤها: {orderPreview.eligibleCount}</p>
                <p>مستوردة مسبقاً: {orderPreview.alreadyImportedCount}</p>
                <p>غير مؤهلة: {orderPreview.ineligibleCount}</p>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {(orderPreview.pieces || []).map((piece) => (
                    <p key={`${piece.orderDetailsId}-${piece.unitIndexInLine}`} className="text-slate-600">
                      {piece.designCode} / {displayAr(piece.typeName)} / {displayAr(piece.caseStyleName) || "شفاف"} — قطعة {piece.unitIndexInLine}
                      {piece.alreadyImported ? " (موجود)" : ""}
                    </p>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowOrder(false)} className="px-4 py-2 rounded-xl bg-slate-100">
                إلغاء
              </button>
              <button
                type="button"
                disabled={fromOrderMutation.isPending || !orderIdInput}
                onClick={() => fromOrderMutation.mutate()}
                className="px-4 py-2 rounded-xl bg-sky-600 text-white font-semibold"
              >
                إنشاء المرتجعات
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showDeleteAll && (
        <Modal title="حذف كل المرتجعات المتاحة" onClose={() => setShowDeleteAll(false)}>
          <div className="space-y-3">
            <p className="text-red-700 font-semibold">
              هذا الإجراء لا يمكن التراجع عنه بسهولة. سيتم حذف المرتجعات المتاحة فقط، مع الإبقاء على المحجوز والمستهلك.
            </p>
            <Field label="اكتب رمز التأكيد للمتابعة">
              <input
                className="w-full p-2.5 border rounded-xl"
                value={deleteAllInput}
                onChange={(e) => setDeleteAllInput(e.target.value)}
                autoComplete="off"
              />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowDeleteAll(false)} className="px-4 py-2 rounded-xl bg-slate-100">
                إلغاء
              </button>
              <button
                type="button"
                disabled={deleteAllMutation.isPending || deleteAllInput !== DELETE_ALL_CODE}
                onClick={() => deleteAllMutation.mutate(deleteAllInput)}
                className="px-4 py-2 rounded-xl bg-red-600 text-white font-semibold disabled:opacity-40"
              >
                تأكيد الحذف
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold">{title}</h3>
        <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700">
          ×
        </button>
      </div>
      {children}
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block text-sm">
    <span className="block mb-1 font-medium text-slate-700">{label}</span>
    {children}
  </label>
);

export default ReturnsPage;
